import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FlaskConical, Save, Send, ShieldCheck, RotateCcw, RefreshCw, AlertCircle, Beaker, ClipboardList, FileText, Loader2, FilePlus, Lock, CheckCircle2, Ban, Sprout } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import EvidencePhotosPanel from "@/pages/admin/EvidencePhotosPanel";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";
import { AiSummaryPanel } from "@/components/AiSummaryPanel";
import { CustomerSummaryInfoCard } from "@/components/CustomerSummaryInfoCard";
import { ReportActions } from "@/components/ReportActions";
import { CustomerRequestsAdminPanel } from "@/components/CustomerRequestsAdminPanel";
import { CreateProposalFromTestCard } from "@/components/CreateProposalFromTestCard";
import { ProposalRequestStatusCard } from "@/components/ProposalRequestStatusCard";
import { QuestionnaireEditPanel } from "@/components/admin/QuestionnaireEditPanel";
import { ReopenForCorrectionCard } from "@/components/admin/ReopenForCorrectionCard";
import { Bore360LabCard } from "@/components/admin/Bore360LabCard";
import { LsiCalculationCard } from "@/components/admin/LsiCalculationCard";
import {
  summariseQuestionnaireAuditEntry,
  isQuestionnaireEditEntry,
} from "@/components/admin/questionnaireDiff";
import { recordVisit } from "@/lib/recentVisits";

const PRESENCE_OPTIONS = ["Negative", "Positive", "Not Tested"];
const NT_VALUE = "Not Tested";

// Lab-staff context panel for samples that originated from a Property
// Campaign Water Health Check — inspection summary + report link.
function InspectionContextPanel({ bookingRef }) {
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    if (!bookingRef) return;
    api.get(`/lab/property-inspection-context/${bookingRef}`)
      .then(({ data }) => setCtx(data)).catch(() => setCtx({ available: false }));
  }, [bookingRef]);
  if (!ctx?.available) return null;
  const i = ctx.inspection;
  const b = ctx.booking;
  const sendReminder = async () => {
    try {
      const { data } = await api.post(`/admin/field-bookings/${bookingRef}/payment-reminder`);
      toast.success(`Payment reminder sent to ${data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reminder failed"); }
  };
  const Row = ({ label, value }) => (
    <div><span className="text-slate-500">{label}:</span> <span className="font-semibold text-slate-800">{value || "—"}</span></div>
  );
  return (
    <section data-testid="lab-inspection-context" className="rounded-3xl bg-cyan-50 border border-cyan-200 p-5">
      {b?.technician_collected && (
        <div data-testid="lab-tech-collected-banner"
          className={`mb-3 rounded-2xl border p-3 ${b.on_hold_payment ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-300"}`}>
          <div className={`text-[10px] uppercase tracking-[0.15em] font-bold ${b.on_hold_payment ? "text-amber-700" : "text-emerald-700"}`}>
            Technician Collected — No Customer Return Required
          </div>
          <div className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <Row label="Sample source" value={b.sample_source} />
            <Row label="Collected by" value={b.sample_collected_by} />
            <Row label="Collection method" value="Technician collected" />
            <Row label="Collected" value={b.sample_collected_at ? new Date(b.sample_collected_at).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"} />
            <Row label="Payment" value={b.payment_status === "paid" ? "Paid" : "Pending"} />
            <Row label="Customer return required" value="No" />
          </div>
          {b.on_hold_payment && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold text-amber-700" data-testid="lab-on-hold-payment">ON HOLD — PAYMENT REQUIRED · Do not commence testing</span>
              <button data-testid="lab-send-payment-reminder" onClick={sendReminder}
                className="px-3 py-1.5 rounded-full bg-amber-600 text-white text-xs font-bold">SEND PAYMENT REMINDER</button>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">Property Inspection Available</div>
          <div className="font-bold text-slate-900 mt-0.5">Water Health Inspection — {i.completed_at ? new Date(i.completed_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "—"}</div>
        </div>
        <button data-testid="lab-view-inspection" onClick={async () => {
          try {
            const { data: blob } = await api.get(i.report_pdf_url.replace("/api", ""), { responseType: "blob" });
            window.open(URL.createObjectURL(new Blob([blob], { type: "application/pdf" })), "_blank");
          } catch { /* report not yet approved */ }
        }} className="px-4 py-2 rounded-full bg-cyan-700 text-white text-xs font-bold">VIEW INSPECTION</button>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <Row label="Sediment" value={i.sediment_level} />
        <Row label="Clarity" value={i.water_clarity} />
        <Row label="Colour" value={i.water_colour} />
        <Row label="Odour" value={i.odour} />
        <Row label="Gutters" value={i.gutter_condition} />
        <Row label="Lead flashing" value={i.lead_flashing} />
        <Row label="Filtration installed" value={i.filtration_installed} />
        <Row label="UV installed" value={i.uv_installed} />
        <Row label="Testing history" value={i.testing_history} />
        <Row label="Sample source" value={i.sample_source} />
        <Row label="Technician" value={i.technician} />
      </div>
      {(i.photos || []).length > 0 && (
        <div className="mt-2 text-xs text-slate-600" data-testid="lab-inspection-photos">
          {i.photos.length} inspection photo{i.photos.length > 1 ? "s" : ""} — {i.photos.map((p) => p.finding ? `${p.category.replace(/_/g, " ")} (${p.finding})` : p.category.replace(/_/g, " ")).join(" · ")} — open the report to view them in context.
        </div>
      )}
      {(i.customer_concerns || []).length > 0 && (
        <div className="mt-2 text-xs"><span className="text-slate-500">Customer concerns:</span> <span className="font-semibold text-slate-800">{i.customer_concerns.join(" · ")}</span>{i.customer_concerns_other && <span className="text-slate-600"> — "{i.customer_concerns_other}"</span>}</div>
      )}
    </section>
  );
}

function statusLabel(s) {
  return ({
    pending_sample: "Pending sample",
    received: "Received",
    results_entered: "Results entered",
    awaiting_review: "Awaiting review",
    approved: "Approved",
    published: "Published",
    cancelled: "Cancelled",
    draft: "Draft (legacy)",
  })[s] || s;
}

/**
 * Turn raw audit_log action strings (snake_case) into staff-friendly labels.
 * Backend data shape is untouched — this is purely for display.
 * Unknown actions fall back to Title Case so we never show snake_case.
 */
const AUDIT_LABELS = {
  approve_and_send_started:   "Approve & Send started",
  approve_and_send_completed: "Approved & sent to customer",
  report_email_failed:        "Email delivery failed",
  report_email_sent:          "Report email sent",
  report_generated:           "Report generated",
  report_regenerated:         "Report regenerated",
  submitted_for_review:       "Submitted for admin review",
  results_entered:            "Results entered",
  results_approved:           "Results approved",
  report_approved:            "Report approved",
  property_details_updated:   "Property details updated",
  created_from_test:          "Estimate created from test",
  received:                   "Sample received",
  approved:                   "Test approved",
  published:                  "Report published",
  ai_summary_generated:       "AI Summary generated",
  ai_summary_regenerated:     "AI Summary regenerated",
  ai_summary_edited:          "AI Summary edited",
  ai_summary_approved:        "AI Summary approved",
  ai_summary_unapproved:      "AI Summary unapproved",
  property_info_edited:       "Customer questionnaire updated",
  bore360_override_set:       "Bore 360 level overridden",
  bore360_override_cleared:   "Bore 360 override cleared",
  report_sms_sent:                    "Customer SMS sent",
  report_sms_failed:                  "Customer SMS failed",
  report_sms_skipped_no_mobile:       "Customer SMS skipped — no mobile on file",
  report_sms_skipped_invalid_mobile:  "Customer SMS skipped — invalid mobile",
  report_sms_skipped_not_configured:  "Customer SMS skipped — SMS not configured",
  report_sms_skipped_paused_recipient: "Customer SMS skipped — recipient paused",
  report_sms_skipped_duplicate:       "Customer SMS already sent (duplicate suppressed)",
};
function humaniseAuditAction(action) {
  if (!action) return "—";
  if (AUDIT_LABELS[action]) return AUDIT_LABELS[action];
  return action
    .split("_")
    .map((w) => w ? w[0].toUpperCase() + w.slice(1) : "")
    .join(" ");
}

function fromParsed(parsed, key, type) {
  // Below-LOR qualifier (e.g. manganese "<0.1") — redisplay the qualifier,
  // not the stored 0.0 sentinel.
  const q = parsed?.[`${key}_qualifier`];
  if (q) return String(q);
  const v = parsed?.[key];
  if (v === undefined || v === null) return "";
  if (type === "presence") return v ? "Positive" : "Negative";
  return String(v);
}

export default function AdminLabDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isLabTech = user?.role === "lab_technician";
  // The new admin-controlled permission (loaded once on mount) determines
  // whether a lab tech sees Approve & Publish buttons or just the Submit for
  // Admin Review button. Always falsy for non-staff. Always overridden to
  // false by the backend when ai_summary.requires_stephen_review is true.
  const [platformSettings, setPlatformSettings] = useState(null);
  useEffect(() => {
    if (isAdmin || isLabTech) {
      api.get("/admin/platform-settings").then(({ data }) => setPlatformSettings(data)).catch(() => {});
    }
  }, [isAdmin, isLabTech]);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [form, setForm] = useState({});
  // Set of param keys explicitly marked as "Not Tested" by the lab tech.
  const [notTested, setNotTested] = useState(new Set());
  const [ntReasons, setNtReasons] = useState({}); // {key: reason string}
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [sampleForm, setSampleForm] = useState({ sample_number: "", sample_condition_notes: "", internal_notes: "" });
  // Suggested next sample number — populated alongside the auto-fill in load().
  // Stored separately so the hint can keep showing the suggestion even after
  // the user has typed something custom into the field.
  const [sampleNumberSuggestion, setSampleNumberSuggestion] = useState(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  // Feb 2026 — Agricultural Water Suitability Addendum (historical reports
  // only — never touches the original COA; see routers/agricultural_addenda.py).
  const [agAddendaPanelOpen, setAgAddendaPanelOpen] = useState(false);
  const [agAddenda, setAgAddenda] = useState([]);
  const [agAddendaBusy, setAgAddendaBusy] = useState(false);
  const [agAddendumUses, setAgAddendumUses] = useState(["agricultural_irrigation"]);
  const [agAddendumIrrigationCategory, setAgAddendumIrrigationCategory] = useState("not_specified");
  const [agAddendumSpecies, setAgAddendumSpecies] = useState([]);
  const [reportMeta, setReportMeta] = useState(null);
  // Latest AI summary metadata for the readiness chip + send-button gate.
  // 404 (no summary yet) is normalised to null. Status string is one of
  // "draft" / "edited" / "approved" / "not_generated".
  const [aiSummaryMeta, setAiSummaryMeta] = useState(null);

  /**
   * Open the standalone Official COA PDF in a new tab (no Summary Report).
   * Used by trade / wholesale / partner / property manager accounts that
   * only want the formal certificate.
   */
  const handlePreviewCoaOnly = async () => {
    setPreviewing(true);
    try {
      const { data: pdfBlob } = await api.get(`/admin/reports/${id}/coa-preview`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `scwt-coa-${id.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Official COA opened — no email was sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "COA preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  /**
   * Open the COA + Summary Report PDF preview in a new tab.
   *
   * Hits `/api/admin/reports/{id}/preview` which renders a non-persisted,
   * non-emailing PDF using the current results + latest AI draft. Available
   * to admin AND lab_technician — including when `requires_stephen_review`
   * is set (preview is read-only, the send guardrail still blocks them).
   */
  const handlePreviewPdf = async () => {
    setPreviewing(true);
    try {
      const { data: pdfBlob } = await api.get(`/admin/reports/${id}/preview`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        // Popup blocker → fall back to a download
        const a = document.createElement("a");
        a.href = url;
        a.download = `scwt-preview-${id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke after a short delay so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Preview opened — no email was sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  /**
   * Download the Pristine Water Systems Plain-English Water Test Summary PDF
   * populated from this test record. Lab-staff only. Does not persist or
   * send anything — same gather-for-preview path as the Aware report
   * preview, so it works on any test with results entered.
   */
  const handleDownloadPristineSummary = async () => {
    setPreviewing(true);
    try {
      const { data: pdfBlob } = await api.get(
        `/admin/reports/${id}/pristine-summary`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `pristine-summary-${id.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Pristine summary opened — no email was sent");
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail) || "Pristine summary failed",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [res, specsRes] = await Promise.all([
        api.get(`/lab/tests/${id}`),
        api.get("/lab/field-specs"),
      ]);
      setData(res.data);
      setSpecs(specsRes.data);
      const t = res.data.test;
      // Seed the form from EVERY parameter applicable to this test type
      // (grouped catalog), not just the legacy required-fields list.
      const catalog = specsRes.data.catalog?.[t.test_id]
        || specsRes.data.catalog?.tank
        || { groups: [] };
      const initial = {};
      for (const g of catalog.groups || []) {
        for (const p of g.params || []) {
          initial[p.key] = fromParsed(t.parsed_results, p.key, p.type);
        }
      }
      setForm(initial);
      setNotTested(new Set(t.not_tested || []));
      setNtReasons(t.not_tested_reasons || {});
      setSubmitAttempted(false);
      setSampleForm({
        sample_number: t.sample_number || "",
        sample_condition_notes: t.sample_condition_notes || "",
        internal_notes: t.internal_notes || "",
      });
      // For pending-intake records without a sample number yet, fetch the
      // next sequential suggestion (SCWT-####). Tucked behind a try so a
      // network blip never blocks the lab detail render.
      if (!t.sample_number && (t.status === "pending_sample" || !t.status)) {
        try {
          const { data: next } = await api.get("/lab/sample-number/next");
          setSampleNumberSuggestion(next);
          // Only fill if user hasn't already typed something
          setSampleForm((sf) => sf.sample_number ? sf : { ...sf, sample_number: next.sample_number });
        } catch (_) { /* silent — manual entry still works */ }
      }
      setInternalNotes(t.internal_notes || "");
      // Fetch the latest generated report meta to drive the "results
      // changed" warning + report status pill. Silently no-op when there
      // isn't one yet (404 just means submit/approve hasn't fired).
      try {
        const { data: meta } = await api.get(`/admin/reports/${id}`);
        setReportMeta(meta);
      } catch (_) { setReportMeta(null); }
      // Fetch the latest AI summary metadata to drive the readiness chip
      // beside the Send to Customer button. 404 = no summary yet (normal
      // for tests that just moved to awaiting_review / approved).
      try {
        const { data: ai } = await api.get(`/admin/ai-summaries/${id}`);
        setAiSummaryMeta(ai);
      } catch (_) { setAiSummaryMeta(null); }
      // Record visit for the command-palette "Recently viewed" cache.
      const labelRef = t.sample_number || t.booking_reference || t.id.slice(0, 8);
      recordVisit({
        kind: "lab_test",
        id: t.id,
        label: `Sample ${labelRef}`,
        sublabel: `${(t.status || "").replace(/_/g, " ")} · ${t.test_id || "test"}`,
        to: `/admin/lab/${t.id}`,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load test");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // ?focus=intake — invoked from the "Open lab record" deep-link on the
  // Create Booking success screen. Scrolls the Sample intake panel into
  // view, focuses the first input (sample number), and briefly pulses a
  // cyan ring so the user knows where they are. Runs ONCE per mount after
  // data loads, guarded by a ref so React StrictMode + re-renders don't
  // cancel the animation halfway through.
  const focusFlag = searchParams.get("focus");
  const intakeFocused = useRef(false);
  useEffect(() => {
    if (intakeFocused.current) return;
    if (focusFlag !== "intake" || loading || !data) return;
    const node = document.querySelector("[data-testid='lab-intake-section']");
    if (!node) return;
    intakeFocused.current = true;
    // Defer one frame so layout settles before scrolling. Use inline
    // styles (not Tailwind classes) — JIT can't statically detect class
    // names added via classList at runtime.
    setTimeout(() => {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
      node.style.transition = "box-shadow 400ms ease-out";
      node.style.boxShadow  = "0 0 0 4px rgba(34, 211, 238, 0.55)";
      setTimeout(() => { node.style.boxShadow = ""; }, 1800);
      const input = node.querySelector("[data-testid='lab-input-sample-number']");
      if (input) try { input.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
    }, 80);
    // Strip the flag so refreshing the page doesn't re-trigger
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, focusFlag]);

  if (loading || !data || !specs) {
    return <PortalShell title="Loading..." subtitle="Lab"><div className="text-slate-500">Loading...</div></PortalShell>;
  }

  const { test, property, booking } = data;
  const catalog = specs.catalog?.[test.test_id] || specs.catalog?.tank || { name: test.test_name, groups: [] };
  const required = specs.required_by_test[test.test_id] || [];
  // A required parameter is "complete" if either:
  //  - a non-empty result value has been entered, OR
  //  - the lab tech has intentionally marked it as Not Tested.
  const isFieldComplete = (k) =>
    notTested.has(k) || (form[k] !== "" && form[k] !== undefined && form[k] !== NT_VALUE);
  const missingRequired = required.filter((k) => !isFieldComplete(k));
  const cust = booking?.customer || {};
  // The new admin-controlled permission determines whether a lab tech sees
  // the Approve & Send buttons or only "Submit for Admin Review". Always
  // overridden to false when ai_summary.requires_stephen_review is true.
  // Escalation flag — sourced from the latest AI summary doc fetched above.
  // The backend also enforces this at /approve-and-send so the chip is
  // Phase 2J-M: the AI / Claude pipeline no longer gates lab-tech approval.
  // `aiSummaryMeta` is still loaded best-effort (for the legacy admin
  // disclosure panel) but never blocks the workflow.
  const labTechMayApprove = isLabTech
    && !!platformSettings?.lab_tech_can_approve_and_send_reports;
  const isManager = isAdmin || labTechMayApprove;
  const canReceive = test.status === "pending_sample";
  const canSaveResults = ["received", "results_entered"].includes(test.status);
  const canSubmit = ["received", "results_entered"].includes(test.status) && missingRequired.length === 0;
  const canApprove = isManager && (test.status === "awaiting_review" || test.status === "approved");
  const canReject = isManager && test.status === "awaiting_review";

  // === Send-to-Customer readiness chip ===
  // One compact pill rendered beside the Send button so the lab team gets
  // an instant cue. Precedence (most restrictive first):
  //   1. stephen_required  — AI flagged the result; lab tech is hard-blocked
  //   2. regenerate        — results changed since the Summary was generated
  //   3. generate_first    — no AI summary exists yet
  //   4. ready             — Summary exists, fresh, no escalation
  // The actual `disabled` decision on the Send button is derived from this
  // state so we only have one source of truth.
  // Send-readiness state machine. Phase 2J-M: AI / Claude approval is
  // no longer required. The customer-facing report is built from the
  // deterministic Pristine Water Test Summary & Action Plan, so the
  // only blocker for "send" is a stale report payload — admins simply
  // regenerate, then send.
  let sendReadiness;
  if (reportMeta?.is_stale) {
    sendReadiness = {
      state: "regenerate",
      label: "Regenerate Customer Report",
      tone: "amber",
      icon: "warn",
      detail: "Results have changed. Regenerate the Customer Report before sending.",
      blocksSend: true,
    };
  } else {
    sendReadiness = {
      state: "ready",
      label: "Customer Report ready",
      tone: "emerald",
      icon: "check",
      detail: "Customer Report is current — safe to send.",
      blocksSend: false,
    };
  }
  const sendBlockedByReadiness = sendReadiness.blocksSend;

  // Build the request body for save/submit — strip NT values from `results`
  // so the backend sees an unambiguous "intentionally not tested" signal.
  const buildResultsPayload = () => {
    const cleanResults = {};
    for (const [k, v] of Object.entries(form)) {
      if (!notTested.has(k) && v !== "" && v !== undefined && v !== NT_VALUE) {
        cleanResults[k] = v;
      }
    }
    return {
      results: cleanResults,
      not_tested: Array.from(notTested),
      not_tested_reasons: Object.fromEntries(
        Array.from(notTested).map((k) => [k, ntReasons[k] || ""]),
      ),
      internal_notes: internalNotes,
    };
  };

  // Bulk-action: flip every currently-empty parameter to "Not Tested" with a
  // pre-filled reason. Saves a dozen clicks when a partial sample arrives.
  const markEmptyAsNotTested = () => {
    const reason = "Insufficient sample volume";
    const toFlip = [];
    for (const g of catalog.groups || []) {
      for (const p of g.params || []) {
        const k = p.key;
        if (notTested.has(k)) continue;
        const v = form[k];
        if (v === "" || v === undefined || v === null) toFlip.push(k);
      }
    }
    if (toFlip.length === 0) {
      toast.info("No empty fields to mark — every parameter already has a value or is Not Tested.");
      return;
    }
    setNotTested((prev) => {
      const next = new Set(prev);
      toFlip.forEach((k) => next.add(k));
      return next;
    });
    setNtReasons((prev) => {
      const next = { ...prev };
      toFlip.forEach((k) => { if (!next[k]) next[k] = reason; });
      return next;
    });
    setForm((f) => {
      const next = { ...f };
      toFlip.forEach((k) => { next[k] = ""; });
      return next;
    });
    toast.success(`Marked ${toFlip.length} empty field${toFlip.length === 1 ? "" : "s"} as Not Tested (reason: "${reason}")`);
  };

  // Toggle "Not Tested" on a single param. When ON, clear the value field.
  const toggleNotTested = (key) => {
    const next = new Set(notTested);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
      setForm((f) => ({ ...f, [key]: "" }));
    }
    setNotTested(next);
  };

  const handleReceive = async () => {
    if (!sampleForm.sample_number.trim()) {
      toast.error("Please enter a sample number");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/lab/tests/${id}/receive`, sampleForm);
      toast.success(`Sample ${sampleForm.sample_number} received`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not mark received");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await api.post(`/lab/tests/${id}/results`, buildResultsPayload());
      toast.success("Draft results saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (missingRequired.length > 0) {
      // Scroll to the first missing required field
      const first = document.querySelector(`[data-testid="lab-field-${missingRequired[0]}"]`);
      if (first) {
        const card = first.closest("div");
        (card || first).scrollIntoView({ behavior: "smooth", block: "center" });
      }
      toast.error(`${missingRequired.length} required field${missingRequired.length === 1 ? " is" : "s are"} still missing.`);
      return;
    }
    setSaving(true);
    try {
      // Auto-save first
      await api.post(`/lab/tests/${id}/results`, buildResultsPayload());
      await api.post(`/lab/tests/${id}/submit`);
      toast.success("Submitted for manager review");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.errors) toast.error(detail.errors.join(" · "));
      else toast.error(formatApiError(detail) || "Could not submit");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (publish, { force = false } = {}) => {
    setSaving(true);
    try {
      if (publish) {
        // Consolidated approve-and-send endpoint — atomic on the backend
        // so we can't end up approved-but-unsent on a flaky connection.
        // Per spec: approval is preserved even if the send step fails so
        // admin can retry from the Lab Detail page later.
        const { data } = await api.post(
          `/admin/reports/${id}/approve-and-send${force ? "?force=true" : ""}`
        );
        // Feb 2026 — server-side 60s dedup guard. If the same report was
        // already emailed within the cooldown, the outer endpoint bubbles
        // up `duplicate_suppressed: true`. Prompt admin so they don't
        // silently re-send duplicates and can override intentionally.
        if (data.duplicate_suppressed) {
          const age = Math.round(data.seconds_since_last_send || 0);
          const ok = window.confirm(
            `⚠  Duplicate send blocked\n\n` +
            `This report was already emailed to ${data.to || "the customer"} ${age} second(s) ago.\n\n` +
            `Sending it again right now will deliver a second copy of the same email.\n\n` +
            `Click OK only if you're sure you want to re-send.`
          );
          if (ok) {
            await handleApprove(publish, { force: true });
            return;
          }
          toast.info(`Send skipped — already emailed ${age}s ago`);
          await load();
          return;
        }
        if (data.ok && data.sent) {
          // Surface the regen step so admin knows the fresh PDF was used
          if (data.regenerated) {
            toast.success("Approved & sent — report regenerated with latest results");
          } else if (data.approved) {
            toast.success("Approved & sent to customer");
          } else {
            // Already-approved retry → just resent the existing PDF
            toast.success("Report sent to customer");
          }
        } else if (data.approved && !data.sent) {
          const msg = data.regenerated
            ? "Report approved and regenerated with latest results, but email delivery failed. You can retry sending once the email issue is resolved."
            : (data.retry_hint || "Approved successfully, but the customer email could not be sent — check /admin/readiness and try Send again.");
          toast.warning(msg);
        } else {
          toast.error(data.error || "Approve & Send failed");
        }
      } else {
        await api.post(`/lab/tests/${id}/approve`, { publish: false });
        toast.success("Approved (not yet sent to customer)");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not approve");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Manually regenerate the customer-final PDF. The submit + approve
   * endpoints already auto-fire this in the background, but admin needs an
   * explicit button for when results were corrected after generation
   * (see the "results changed" warning banner).
   */
  const handleRegenerateReport = async () => {
    setRegenerating(true);
    try {
      await api.post(`/admin/reports/${id}/generate`);
      toast.success("Summary Report regenerated with latest results");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not regenerate report");
    } finally {
      setRegenerating(false);
    }
  };

  /**
   * "Recalculate & Rebuild Report" — admin-only. Unlike Regenerate (which
   * only re-renders the PDF from whatever recommendations/report_facts
   * snapshot already exists), this re-runs Health Score, report_facts and
   * recommendations against the CURRENT guideline code, then rebuilds the
   * combined PDF as a new version. Raw lab results and status are never
   * touched. Confirm first — this corrects a potentially already-sent
   * published report.
   */
  const handleRecalculateReport = async () => {
    const ok = window.confirm(
      "Recalculate & Rebuild Report\n\n" +
      "This re-runs Health Score, report facts and recommendations against " +
      "the current ADWG guideline rules, then rebuilds the Summary Report PDF " +
      "as a new version. Raw laboratory results are never changed.\n\n" +
      "Continue?"
    );
    if (!ok) return;
    setRecalculating(true);
    try {
      const { data } = await api.post(`/admin/reports/${id}/recalculate`);
      const { before, after } = data;
      toast.success(
        `Recalculated: ${before.band || "—"} → ${after.band || "—"} `
        + `(${before.overall_status || "—"} → ${after.overall_status || "—"})`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not recalculate report");
    } finally {
      setRecalculating(false);
    }
  };

  const AG_ADDENDUM_USE_OPTS = [
    { v: "agricultural_irrigation", l: "Irrigation — agricultural crops" },
    { v: "garden_irrigation", l: "Irrigation — gardens & landscaping" },
    { v: "livestock_watering", l: "Livestock drinking water" },
  ];
  const AG_IRRIGATION_CATEGORY_OPTS = [
    { v: "not_specified", l: "Not sure / general (most conservative)" },
    { v: "general_garden_landscaping", l: "Garden & landscaping" },
    { v: "vegetable_crops", l: "Vegetable crops" },
    { v: "pasture_fodder", l: "Pasture / fodder" },
    { v: "orchard_vine_tree_crops", l: "Orchard, vine or tree crops" },
    { v: "broadacre_field_crops", l: "Broadacre / field crops" },
  ];
  const AG_SPECIES_OPTS = ["cattle_beef", "cattle_dairy", "sheep_adult", "sheep_weaner", "horses", "pigs", "poultry", "goats", "not_specified"];

  const loadAgAddenda = async () => {
    try {
      const { data } = await api.get("/admin/agricultural-addenda", { params: { test_id: id } });
      setAgAddenda(data.addenda || []);
    } catch (e) { /* non-fatal */ }
  };

  const handleOpenAgAddendaPanel = () => {
    setAgAddendaPanelOpen((v) => !v);
    if (!agAddendaPanelOpen) loadAgAddenda();
  };

  const handleCreateAgAddendum = async () => {
    setAgAddendaBusy(true);
    try {
      await api.post("/admin/agricultural-addenda", {
        test_id: id,
        agricultural_intended_uses: agAddendumUses,
        irrigation_category: agAddendumUses.some((u) => u === "garden_irrigation" || u === "agricultural_irrigation") ? agAddendumIrrigationCategory : null,
        livestock_species: agAddendumUses.includes("livestock_watering") ? agAddendumSpecies : [],
      });
      toast.success("Agricultural addendum prepared as a Draft — review and approve it before sending to the customer.");
      await loadAgAddenda();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not prepare addendum");
    } finally {
      setAgAddendaBusy(false);
    }
  };

  const handleApproveAgAddendum = async (addendumId) => {
    setAgAddendaBusy(true);
    try {
      await api.post(`/admin/agricultural-addenda/${addendumId}/approve`);
      toast.success("Addendum approved");
      await loadAgAddenda();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not approve");
    } finally {
      setAgAddendaBusy(false);
    }
  };

  const handleSendAgAddendum = async (addendumId) => {
    const ok = window.confirm("Send this approved agricultural addendum to the customer by email now?");
    if (!ok) return;
    setAgAddendaBusy(true);
    try {
      await api.post(`/admin/agricultural-addenda/${addendumId}/send`);
      toast.success("Addendum emailed to customer");
      await loadAgAddenda();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send");
    } finally {
      setAgAddendaBusy(false);
    }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      await api.post(`/lab/tests/${id}/reject`, { notes: "Returned to tech for revision" });
      toast.info("Sent back to lab tech");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reject");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalShell title="Test detail" subtitle="Laboratory · Results entry">
      <Link to="/admin/lab" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-5">
        <ArrowLeft className="w-4 h-4" /> All tests
      </Link>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* LEFT — main content */}
        <div className="lg:col-span-8 space-y-5">
          {booking?.property_campaign_inspection_id && (
            <InspectionContextPanel bookingRef={test.booking_reference} />
          )}
          {/* Identifiers */}
          <section className="rounded-3xl bg-white border border-slate-200 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-600">{test.test_name}</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1.5 font-mono">{test.booking_reference || "—"}</div>
                {test.sample_number && (
                  <div className="mt-1 text-sm text-cyan-700 font-bold">Sample number: <span className="font-mono">{test.sample_number}</span></div>
                )}
              </div>
              <span data-testid="lab-detail-status" className="px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-900 text-white">
                {statusLabel(test.status)}
              </span>
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Customer</div>
                <div className="font-medium text-slate-900 mt-0.5">{cust.name || "—"}</div>
                <div className="text-xs text-slate-500">{cust.email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Property</div>
                <div className="font-medium text-slate-900 mt-0.5">{property?.nickname || property?.address || "—"}</div>
                <div className="text-xs text-slate-500 capitalize">{property?.property_type}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Sample type</div>
                <div className="font-medium text-slate-900 mt-0.5">{cust.sampleType || "—"}</div>
              </div>
            </div>
          </section>

          {/* Jun 2026 — Bore 360 decision-matrix assessment + lab override.
              Rendered for bore tests once any results exist. The level is
              auto-derived on the backend; lab staff may override it (with
              an audited reason) any time before publish. */}
          {data.bore360_assessment && (
            <Bore360LabCard
              testId={id}
              assessment={data.bore360_assessment}
              status={test.status}
              onChanged={load}
            />
          )}

          {/* Jun 2026 — LSI calculated-index panel (tank/bore packages).
              Backend recomputes on every load so amendments show live. */}
          {data.lsi && <LsiCalculationCard lsi={data.lsi} />}

          {/* Customer questionnaire — Phase 2L-A11. Read-only after report
              has been published / sent, otherwise admins + lab techs can
              correct the customer's answers before the report goes out.
              Mirrors edits to property.intake on save. */}
          {booking && (
            <QuestionnaireEditPanel booking={booking} test={test} onSaved={load} />
          )}

          {/* Sample intake */}
          {canReceive && (
            <section data-testid="lab-intake-section" className="rounded-3xl bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Beaker className="w-4 h-4 text-cyan-600" />
                <h2 className="font-display text-lg font-semibold text-slate-900">Sample intake</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Sample number</Label>
                  <Input
                    data-testid="lab-input-sample-number"
                    value={sampleForm.sample_number}
                    onChange={(e) => setSampleForm({ ...sampleForm, sample_number: e.target.value })}
                    placeholder="e.g. 1001 or SCWT-1001"
                    className="mt-1.5 h-11 rounded-xl"
                  />
                  {sampleNumberSuggestion && (
                    <p data-testid="lab-sample-number-hint" className="mt-1.5 text-[11px] text-slate-500">
                      {sampleNumberSuggestion.previous_highest >= 1001 && (
                        <>Last assigned: <span className="font-mono">SCWT-{sampleNumberSuggestion.previous_highest}</span> · </>
                      )}
                      Suggested: <span className="font-mono">{sampleNumberSuggestion.sample_number}</span>
                      <span className="text-slate-400"> · editable</span>
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Sample condition notes</Label>
                  <Textarea
                    data-testid="lab-input-condition"
                    value={sampleForm.sample_condition_notes}
                    onChange={(e) => setSampleForm({ ...sampleForm, sample_condition_notes: e.target.value })}
                    placeholder="e.g. Container intact, temperature ~6°C on arrival"
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Internal laboratory notes (not customer visible)</Label>
                  <Textarea
                    data-testid="lab-input-intake-internal"
                    value={sampleForm.internal_notes}
                    onChange={(e) => setSampleForm({ ...sampleForm, internal_notes: e.target.value })}
                    className="mt-1.5 rounded-xl"
                  />
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <Button data-testid="lab-btn-receive" onClick={handleReceive} disabled={saving} className="btn-aqua rounded-full font-semibold">
                  <Beaker className="w-4 h-4 mr-1.5" /> Mark sample received
                </Button>
              </div>
            </section>
          )}

          {/* Results entry */}
          {!canReceive && (
            <section data-testid="lab-results-section" data-test-id-type={test.test_id} className="rounded-3xl bg-white border border-slate-200 p-6">
              <div className="flex items-start justify-between gap-3 mb-1">
                <div>
                  <div className="flex items-center gap-2">
                    <FlaskConical className="w-4 h-4 text-cyan-600" />
                    <h2 className="font-display text-lg font-semibold text-slate-900">Laboratory results</h2>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Showing parameters for <strong>{catalog.name}</strong>. Required fields are marked with <span className="text-rose-500">*</span>. You can save partial results at any time — submit for review only locks the test once every required field is filled.
                  </p>
                </div>
                {canSaveResults && (() => {
                  const emptyCount = (catalog.groups || []).reduce((acc, g) => acc + g.params.filter((p) => !notTested.has(p.key) && (form[p.key] === "" || form[p.key] === undefined || form[p.key] === null)).length, 0);
                  return emptyCount > 0 ? (
                    <button
                      data-testid="lab-bulk-mark-empty-nt-btn"
                      type="button"
                      onClick={markEmptyAsNotTested}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-semibold bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-700 border border-slate-200 transition-colors"
                      title="Flip every empty field to Not Tested with reason 'Insufficient sample volume'"
                    >
                      <Beaker className="w-3.5 h-3.5" /> Mark {emptyCount} empty field{emptyCount === 1 ? "" : "s"} as Not Tested
                    </button>
                  ) : null;
                })()}
              </div>
              {canSaveResults && missingRequired.length > 0 && (
                <div data-testid="lab-required-missing" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{missingRequired.length}</strong> required field{missingRequired.length === 1 ? " is" : "s are"} still missing. Please complete the highlighted field{missingRequired.length === 1 ? "" : "s"} before submitting for review — or mark them as <strong>Not Tested</strong> if they were not measured.
                  </span>
                </div>
              )}

              <div className="space-y-7">
                {(catalog.groups || []).map((group) => {
                  const groupMissing = group.params.filter((p) => required.includes(p.key) && !isFieldComplete(p.key)).length;
                  return (
                  <div key={group.label} data-testid={`lab-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex items-center justify-between pb-1.5 border-b border-cyan-100 mb-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">{group.label}</div>
                      {groupMissing > 0 && submitAttempted && (
                        <span data-testid={`lab-group-missing-count`} className="text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">{groupMissing} missing field{groupMissing === 1 ? "" : "s"}</span>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {group.params.map((spec) => {
                        const key = spec.key;
                        const isRequired = !!spec.required;
                        const isNT = notTested.has(key);
                        const isMissing = isRequired && !isFieldComplete(key);
                        const showError = submitAttempted && isMissing;
                        const errorRing = showError ? "border-rose-500 ring-1 ring-rose-500" : "";
                        const labelText = spec.type === "presence"
                          ? `${spec.display_name} (Positive/Negative)`
                          : spec.display_name;
                        return (
                          <div key={key}>
                            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700 flex items-center gap-1.5">
                              <span>{labelText}</span>
                              {spec.unit && <span className="text-slate-400 normal-case">({spec.unit})</span>}
                              {isRequired && <span className="text-rose-500">*</span>}
                              {isNT && <span data-testid={`lab-field-nt-pill-${key}`} className="text-[9px] uppercase tracking-wider font-bold text-slate-700 bg-slate-200 px-1.5 py-0.5 rounded-full normal-case">Not Tested</span>}
                            </Label>
                            {spec.type === "presence" ? (
                              <Select
                                value={isNT ? NT_VALUE : (form[key] || "")}
                                onValueChange={(v) => {
                                  if (v === NT_VALUE) {
                                    if (!notTested.has(key)) toggleNotTested(key);
                                  } else {
                                    if (notTested.has(key)) toggleNotTested(key);
                                    setForm((f) => ({ ...f, [key]: v }));
                                  }
                                }}
                              >
                                <SelectTrigger data-testid={`lab-field-${key}`} className={`mt-1.5 h-11 rounded-xl ${errorRing}`}>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {PRESENCE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                data-testid={`lab-field-${key}`}
                                type={key === "manganese" ? "text" : "number"}
                                inputMode="decimal"
                                step="any"
                                value={isNT ? "" : (form[key] || "")}
                                disabled={isNT}
                                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                placeholder={isNT ? "Not Tested" : (key === "manganese" ? "<0.1 or value (e.g. 0.3)" : (spec.max != null ? `${spec.min ?? 0} – ${spec.max}` : (spec.min != null ? `≥ ${spec.min}` : "")))}
                                className={`mt-1.5 h-11 rounded-xl ${errorRing} ${isNT ? "bg-slate-100 text-slate-400" : ""}`}
                              />
                            )}
                            {key === "manganese" && !isNT && (
                              <p data-testid="lab-field-manganese-lor-hint" className="mt-1 text-[11px] text-slate-500 leading-snug">
                                High-range method — enter <b>&lt;0.1</b> for results below the 0.1 mg/L reporting limit.
                                Reported as &lt;0.1 mg/L and never triggers report warnings; 0.1 or higher evaluates as normal.
                              </p>
                            )}
                            {spec.type === "numeric" && (
                              <label data-testid={`lab-field-nt-toggle-${key}`} className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-600 select-none cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isNT}
                                  onChange={() => toggleNotTested(key)}
                                  className="rounded border-slate-300"
                                />
                                Mark as Not Tested
                              </label>
                            )}
                            {isNT && (
                              <Input
                                data-testid={`lab-field-nt-reason-${key}`}
                                type="text"
                                value={ntReasons[key] || ""}
                                onChange={(e) => setNtReasons({ ...ntReasons, [key]: e.target.value })}
                                placeholder="Reason not tested (optional)"
                                className="mt-1.5 h-9 rounded-xl text-xs"
                              />
                            )}
                            {showError && (
                              <div data-testid={`lab-field-error-${key}`} className="mt-1.5 text-[11px] text-rose-700">
                                Required before submitting for review. Enter a value or mark as Not Tested.
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}

                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Internal laboratory notes</Label>
                  <Textarea
                    data-testid="lab-input-internal"
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder="Internal notes about sample anomalies, dilution factors, etc. (not customer visible)"
                    className="mt-1.5 rounded-xl"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
                {/* Preview button — available to any lab staff once results
                    have been entered. Generates the COA + Summary PDF
                    without sending an email and without persisting a
                    versioned report row. Visible during the entire
                    pre-publish review window. */}
                {["results_entered", "awaiting_review", "approved", "published"].includes(test.status) && (
                  <>
                    <Button
                      data-testid="lab-btn-preview-coa"
                      variant="outline"
                      onClick={handlePreviewCoaOnly}
                      disabled={previewing}
                      className="rounded-full font-semibold text-slate-700 border-slate-300 hover:bg-slate-100"
                      title="Open the Official Certificate of Analysis only (no Summary Report). Useful for trade / wholesale / partner accounts."
                    >
                      {previewing
                        ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Generating…</>
                        : <><FileText className="w-4 h-4 mr-1.5" />Download COA Only</>}
                    </Button>
                    <Button
                      data-testid="lab-btn-preview-pdf"
                      variant="outline"
                      onClick={handlePreviewPdf}
                      disabled={previewing}
                      className="rounded-full font-semibold text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                      title="Open the combined COA + Summary Report PDF that would be sent to the customer. No email is sent."
                    >
                      {previewing
                        ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Generating…</>
                        : <><FileText className="w-4 h-4 mr-1.5" />Preview COA &amp; Summary PDF</>}
                    </Button>
                    <Button
                      data-testid="lab-btn-pristine-summary"
                      variant="outline"
                      onClick={handleDownloadPristineSummary}
                      disabled={previewing}
                      className="rounded-full font-semibold text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      title="Open the Pristine Water Systems Plain-English Water Test Summary for this test. Customer-facing summary that interprets the lab results. No email is sent."
                    >
                      {previewing
                        ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Generating…</>
                        : <><FileText className="w-4 h-4 mr-1.5" />Download Pristine Plain-English Summary</>}
                    </Button>
                    {reportMeta && (
                      <Button
                        data-testid="lab-btn-regenerate-report"
                        variant="outline"
                        onClick={handleRegenerateReport}
                        disabled={regenerating}
                        className="rounded-full font-semibold text-slate-700 border-slate-200 hover:bg-slate-50"
                        title="Rebuild the persisted COA + Summary PDF using the latest results."
                      >
                        {regenerating
                          ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Regenerating…</>
                          : <><RotateCcw className="w-4 h-4 mr-1.5" />Regenerate Summary Report</>}
                      </Button>
                    )}
                    {isAdmin && reportMeta && ["approved", "published"].includes(test.status) && (
                      <Button
                        data-testid="lab-btn-recalculate-report"
                        variant="outline"
                        onClick={handleRecalculateReport}
                        disabled={recalculating}
                        className="rounded-full font-semibold text-amber-700 border-amber-200 hover:bg-amber-50"
                        title="Re-run Health Score, report facts and recommendations against the current ADWG rules, then rebuild the PDF as a new version. Raw lab results are never changed."
                      >
                        {recalculating
                          ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Recalculating…</>
                          : <><RefreshCw className="w-4 h-4 mr-1.5" />Recalculate &amp; Rebuild Report</>}
                      </Button>
                    )}
                    {isAdmin && reportMeta && ["approved", "published"].includes(test.status) && (
                      <Button
                        data-testid="lab-btn-ag-addendum-toggle"
                        variant="outline"
                        onClick={handleOpenAgAddendaPanel}
                        className="rounded-full font-semibold text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        title="Prepare a separate, supplementary Agricultural Water Suitability Addendum linked to this COA — never modifies the original report."
                      >
                        <Sprout className="w-4 h-4 mr-1.5" />Agricultural Addendum
                      </Button>
                    )}
                  </>
                )}
                {canSaveResults && (
                  <Button
                    data-testid="lab-btn-save-draft"
                    variant="outline" onClick={handleSaveDraft} disabled={saving}
                    className="rounded-full font-semibold"
                  >
                    <Save className="w-4 h-4 mr-1.5" /> Save draft
                  </Button>
                )}
                {canSaveResults && (
                  <Button
                    data-testid="lab-btn-submit"
                    onClick={handleSubmit} disabled={saving || !canSubmit}
                    className="btn-aqua rounded-full font-semibold"
                  >
                    <Send className="w-4 h-4 mr-1.5" /> {isLabTech && !labTechMayApprove ? "Submit for Admin Review" : "Submit for review"}
                  </Button>
                )}
                {canApprove && (
                  <>
                    {canReject && (
                      <Button
                        data-testid="lab-btn-reject"
                        variant="outline" onClick={handleReject} disabled={saving}
                        className="rounded-full font-semibold text-rose-600 border-rose-200 hover:bg-rose-50"
                      >
                        <RotateCcw className="w-4 h-4 mr-1.5" /> Send back
                      </Button>
                    )}
                    {/* Readiness chip — single source of truth for the lab
                        team's "can I click send?" question. Shows next to
                        the Send button so the answer is always one glance
                        away. */}
                    <div
                      data-testid="lab-send-readiness-chip"
                      data-state={sendReadiness.state}
                      className={
                        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border " +
                        (sendReadiness.tone === "emerald"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : sendReadiness.tone === "amber"
                          ? "bg-amber-50 text-amber-800 border-amber-200"
                          : "bg-rose-50 text-rose-700 border-rose-200")
                      }
                      title={sendReadiness.detail}
                    >
                      {sendReadiness.icon === "check" && <CheckCircle2 className="w-3.5 h-3.5" />}
                      {sendReadiness.icon === "warn"  && <AlertCircle className="w-3.5 h-3.5" />}
                      {sendReadiness.icon === "stop"  && <Ban className="w-3.5 h-3.5" />}
                      {sendReadiness.label}
                    </div>
                    {test.status === "awaiting_review" && (
                      <Button
                        data-testid="lab-btn-approve-only"
                        variant="outline"
                        onClick={() => handleApprove(false)}
                        disabled={saving}
                        className="rounded-full font-semibold text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        title="Mark this report as approved internally without emailing the customer. You can send it later."
                      >
                        <ShieldCheck className="w-4 h-4 mr-1.5" /> Approve (don&rsquo;t send yet)
                      </Button>
                    )}
                    <Button
                      data-testid="lab-btn-approve-publish"
                      onClick={() => setConfirmPublish(true)}
                      disabled={saving || sendBlockedByReadiness}
                      className="rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      title={sendBlockedByReadiness ? sendReadiness.detail : undefined}
                    >
                      <ShieldCheck className="w-4 h-4 mr-1.5" />
                      {test.status === "approved" ? "Send to Customer Now" : "Approve & Send to Customer"}
                    </Button>
                  </>
                )}
                {reportMeta?.is_stale && (test.status === "awaiting_review" || test.status === "approved") && (
                  <div data-testid="lab-report-stale-warning" className="w-full rounded-2xl bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span><strong>Results have changed since this Customer Report was generated.</strong> Please click <em>Regenerate Customer Report</em> before sending so the customer receives the latest data.</span>
                  </div>
                )}
                {isLabTech && !labTechMayApprove && test.status === "awaiting_review" && (
                  <div data-testid="lab-tech-waiting-admin" className="text-xs text-slate-500 italic flex-1">
                    Awaiting admin approval. Lab tech approval is currently disabled — an admin can enable it on /admin/readiness.
                  </div>
                )}
              </div>

              {agAddendaPanelOpen && (
                <div data-testid="lab-ag-addendum-panel" className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sprout className="w-4 h-4 text-emerald-600" />
                    <h3 className="font-display text-base font-semibold text-emerald-900">Agricultural Water Suitability Addendum</h3>
                  </div>
                  <p className="text-xs text-emerald-800 mb-3 leading-relaxed">
                    A separate, supplementary PDF linked to COA <b>{test.booking_reference}</b> — this never modifies, overwrites or
                    regenerates the original Certificate of Analysis or its Bore 360 drinking-water result. It stays as a Draft until
                    you approve it, and sending it to the customer is always a separate, explicit step.
                  </p>

                  {agAddenda.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {agAddenda.map((a) => (
                        <div key={a.id} data-testid={`ag-addendum-row-${a.id}`} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-emerald-100 px-3 py-2.5">
                          <div className="text-xs">
                            <span className={`px-2 py-0.5 rounded-full font-bold mr-2 ${a.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
                              {a.status === "approved" ? "Approved" : "Draft"}
                            </span>
                            {(a.intended_uses_requested || []).join(", ")} · requested by {a.requested_by}
                            {a.sent_to_customer_at && <span className="text-slate-400"> · sent to customer</span>}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Button data-testid={`ag-addendum-pdf-${a.id}`} size="sm" variant="ghost" className="text-xs h-7 underline" onClick={async () => {
                              try {
                                const { data: blob } = await api.get(`/admin/agricultural-addenda/${a.id}/pdf`, { responseType: "blob" });
                                window.open(URL.createObjectURL(new Blob([blob], { type: "application/pdf" })), "_blank", "noopener");
                              } catch (e) { toast.error("Could not open PDF"); }
                            }}>View PDF</Button>
                            {a.status !== "approved" && (
                              <Button data-testid={`ag-addendum-approve-${a.id}`} size="sm" variant="outline" disabled={agAddendaBusy} onClick={() => handleApproveAgAddendum(a.id)} className="rounded-full text-xs h-7">Approve</Button>
                            )}
                            {a.status === "approved" && !a.sent_to_customer_at && (
                              <Button data-testid={`ag-addendum-send-${a.id}`} size="sm" disabled={agAddendaBusy} onClick={() => handleSendAgAddendum(a.id)} className="btn-aqua rounded-full text-xs h-7">Send to customer</Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-2 mb-3">
                    {AG_ADDENDUM_USE_OPTS.map((opt) => {
                      const sel = agAddendumUses.includes(opt.v);
                      return (
                        <label key={opt.v} data-testid={`ag-addendum-use-${opt.v}`} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-xs ${sel ? "border-emerald-500 bg-white font-medium" : "border-emerald-100 bg-white/60"}`}>
                          <input type="checkbox" checked={sel} onChange={() => setAgAddendumUses((u) => u.includes(opt.v) ? u.filter((x) => x !== opt.v) : [...u, opt.v])} />
                          {opt.l}
                        </label>
                      );
                    })}
                  </div>
                  {agAddendumUses.some((u) => u === "garden_irrigation" || u === "agricultural_irrigation") && (
                    <select data-testid="ag-addendum-irrigation-category" value={agAddendumIrrigationCategory} onChange={(e) => setAgAddendumIrrigationCategory(e.target.value)} className="mb-3 w-full text-xs rounded-xl border border-emerald-200 px-3 py-2">
                      {AG_IRRIGATION_CATEGORY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  )}
                  {agAddendumUses.includes("livestock_watering") && (
                    <div className="grid grid-cols-3 gap-1.5 mb-3">
                      {AG_SPECIES_OPTS.map((sp) => {
                        const sel = agAddendumSpecies.includes(sp);
                        return (
                          <label key={sp} data-testid={`ag-addendum-species-${sp}`} className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border cursor-pointer text-[11px] ${sel ? "border-emerald-500 bg-white" : "border-emerald-100 bg-white/60"}`}>
                            <input type="checkbox" checked={sel} onChange={() => setAgAddendumSpecies((s) => s.includes(sp) ? s.filter((x) => x !== sp) : [...s, sp])} />
                            {sp.replace(/_/g, " ")}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <Button data-testid="lab-btn-ag-addendum-create" size="sm" disabled={agAddendaBusy || agAddendumUses.length === 0} onClick={handleCreateAgAddendum} className="btn-aqua rounded-full font-semibold">
                    {agAddendaBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Preparing…</> : "Prepare Addendum (Draft)"}
                  </Button>
                </div>
              )}

              {confirmPublish && (
                <div data-testid="approve-confirm-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
                  <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
                    <h3 className="font-display text-lg font-semibold text-slate-900">Approve &amp; send to customer?</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">You are about to send the final COA and Summary Report to the customer. Please confirm the results and summary have been reviewed.</p>
                    <div className="mt-5 flex justify-end gap-2">
                      <Button data-testid="approve-confirm-cancel" variant="ghost" onClick={() => setConfirmPublish(false)} className="rounded-full">Cancel</Button>
                      <Button
                        data-testid="approve-confirm-send"
                        onClick={async () => { setConfirmPublish(false); await handleApprove(true); }}
                        className="rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700"
                      >
                        <ShieldCheck className="w-4 h-4 mr-1.5" /> Confirm &amp; Send
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {!canSaveResults && test.status === "published" && (
                <div className="mt-5 rounded-2xl bg-purple-50 border border-purple-200 p-4 text-sm text-purple-900">
                  These results are <strong>published</strong> and visible on the customer&apos;s AWARE Water Health Record.
                </div>
              )}

              {/* Phase 2L-A24 — Reopen Report for Correction (admin only) */}
              {isAdmin && (test.status === "approved" || test.status === "published") && (
                <ReopenForCorrectionCard test={test} reload={load} />
              )}

              {/* Phase 2L-A24 — Correction-mode warning banner */}
              {test.correction_of_prior_status && test.status === "results_entered" && (
                <div data-testid="correction-banner" className="mt-5 rounded-2xl bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900">
                  <strong>Report reopened for correction.</strong> Edit results, save changes, regenerate the COA/Summary, then review and republish before sending to the customer.
                </div>
              )}
            </section>
          )}
          {["awaiting_review", "approved", "published"].includes(test.status) && (
            <CustomerSummaryInfoCard testId={test.id} isManager={isManager} />
          )}
          {test.status === "published" && (
            <ReportActions testId={test.id} isPublished={true} summaryApproved={true} isManager={isManager} />
          )}
          {test.status === "published" && (
            <CustomerRequestsAdminPanel testId={test.id} />
          )}
          {test.status === "published" && (isAdmin || isLabTech) && (
            <CreateProposalFromTestCard test={test} isAdmin={isAdmin} navigate={navigate} />
          )}
          {test.status === "published" && (
            <ProposalRequestStatusCard testId={test.id} />
          )}
        </div>

        {/* RIGHT — audit log */}
        <aside className="lg:col-span-4">
          <section className="rounded-3xl bg-white border border-slate-200 p-6 sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-4 h-4 text-slate-600" />
              <h3 className="font-display text-lg font-semibold text-slate-900">Audit log</h3>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {(() => {
                // Merge test audit log + questionnaire edits stored on the
                // booking. Booking-side property_info_edited entries are
                // tagged so we can render the one-line summary helper.
                const testLog = (test.audit_log || []).map((a) => ({ ...a, _source: "test" }));
                const bookingQEdits = ((booking?.audit_log) || [])
                  .filter(isQuestionnaireEditEntry)
                  .map((a) => ({ ...a, _source: "booking" }));
                const merged = [...testLog, ...bookingQEdits]
                  .filter((a) => a && a.at)
                  .sort((x, y) => (x.at > y.at ? -1 : x.at < y.at ? 1 : 0));
                if (merged.length === 0) {
                  return <div className="text-sm text-slate-400">No history yet.</div>;
                }
                return merged.map((a, i) => {
                  const isQEdit = isQuestionnaireEditEntry(a);
                  const summary = isQEdit ? summariseQuestionnaireAuditEntry(a) : null;
                  return (
                    <div
                      key={`${a._source}-${a.at}-${i}`}
                      data-testid={isQEdit ? "lab-audit-entry-questionnaire" : "lab-audit-entry"}
                      className="text-sm border-l-2 border-slate-200 pl-3"
                    >
                      <div className="font-semibold text-slate-900 text-xs uppercase tracking-wider">
                        {humaniseAuditAction(a.action)}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {formatDateTimeAU(a.at)}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {a.user_email || a.actor || "system"} · {a.user_role || a.actor_role || "—"}
                      </div>
                      {summary && (
                        <div
                          data-testid="lab-audit-entry-questionnaire-summary"
                          className="text-xs text-slate-700 mt-1 leading-snug"
                        >
                          {summary}
                        </div>
                      )}
                      {a.edit_reason && (
                        <div className="text-xs text-slate-500 italic mt-1">&ldquo;{a.edit_reason}&rdquo;</div>
                      )}
                      {a.notes && (
                        <div className="text-xs text-slate-500 italic mt-1">&ldquo;{a.notes}&rdquo;</div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            {test.data_source === "synthesised" && (
              <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 inline-flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Legacy synthesised data — re-enter via the Results form to replace with real lab values.
              </div>
            )}
          </section>

          {/* Evidence photos — internal use only */}
          <section className="rounded-3xl bg-white border border-slate-200 p-6 mt-6">
            <EvidencePhotosPanel testId={id} />
          </section>
        </aside>
      </div>
    </PortalShell>
  );
}
